import { Icon } from '@components/icons';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { socialMedia, email } from '@config';

const StyledFooter = styled.footer`
  ${({ theme }) => theme.mixins.flexCenter};
  flex-direction: column;
  height: auto;
  min-height: 70px;
  padding: 30px 15px 100px;
  text-align: center;

  .schedule-meeting {
    margin-bottom: 20px;
    font-size: var(--fz-md);
    color: var(--light-slate);

    a {
      display: inline-block;
      color: #ff2a2a;
      font-weight: 600;
      transition: var(--transition);

      @media (hover: hover) {
        &:hover,
        &:focus-visible {
          transform: translateY(-3px);
          color: #fff;
          text-shadow: 0 0 5px rgba(255, 42, 42, 0.5);
        }
      }
      &:active {
        transform: translateY(-3px);
        color: #fff;
        text-shadow: 0 0 5px rgba(255, 42, 42, 0.5);
      }
    }
  }

  .social-links {
    display: none;

    @media (max-width: 768px) {
      display: block;
      width: 100%;
      max-width: 270px;
      margin: 0 auto 10px;
      color: var(--light-slate);
    }

    ul {
      ${({ theme }) => theme.mixins.flexBetween};
      padding: 0;
      margin: 0;
      list-style: none;

      a {
        padding: 10px;
        color: var(--light-slate);
        transition: var(--transition);
        display: inline-block;

        &:focus {
          outline: none;
          color: var(--light-slate);
          transform: none;
          filter: none;
        }

        @media (hover: hover) {
          &:hover,
          &:focus-visible {
            transform: translateY(-3px);
            color: #ff2a2a;
            filter: drop-shadow(0 0 5px rgba(255, 42, 42, 0.6));
          }
        }
        &:active,
        &.touch-active {
          transform: translateY(-3px);
          color: #ff2a2a;
          filter: drop-shadow(0 0 5px rgba(255, 42, 42, 0.6));
        }

        svg {
          width: 20px;
          height: 20px;
        }
      }
    }
  }

  .email-link-footer {
    display: none;
    margin: 15px 0;
    font-family: var(--font-mono);
    font-size: var(--fz-sm);

    @media (max-width: 768px) {
      display: block;
    }

    a {
      display: inline-block;
      color: var(--light-slate);
      transition: var(--transition);

      &:focus {
        outline: none;
        color: var(--light-slate);
        transform: none;
        text-shadow: none;
      }

      @media (hover: hover) {
        &:hover,
        &:focus-visible {
          transform: translateY(-3px);
          color: #ff2a2a;
          text-shadow: 0 0 8px rgba(255, 42, 42, 0.6);
        }
      }
      &:active,
      &.touch-active {
        transform: translateY(-3px);
        color: #ff2a2a;
        text-shadow: 0 0 8px rgba(255, 42, 42, 0.6);
      }
    }
  }
`;

const StyledCredit = styled.div`
  color: var(--light-slate);
  font-family: var(--font-mono);
  font-size: var(--fz-sm);
  line-height: 1;

  a {
    padding: 10px;
    color: var(--slate);
    transition: var(--transition);
    display: inline-block;

    &:focus {
      outline: none;
      color: var(--slate);
      transform: none;
      text-shadow: none;
      filter: none;
    }

    @media (hover: hover) {
      &:hover,
      &:focus-visible {
        transform: translateY(-3px);
        color: #ff2a2a;
        text-shadow: 0 0 8px rgba(255, 42, 42, 0.6);
        filter: drop-shadow(0 0 5px rgba(255, 42, 42, 0.5));
      }
    }
    &:active,
    &.touch-active {
      transform: translateY(-3px);
      color: #ff2a2a;
      text-shadow: 0 0 8px rgba(255, 42, 42, 0.6);
      filter: drop-shadow(0 0 5px rgba(255, 42, 42, 0.5));
    }
  }

  .github-stats {
    margin-top: 10px;

    & > span {
      display: inline-flex;
      align-items: center;
      margin: 0 7px;
    }
    svg {
      display: inline-block;
      margin-right: 5px;
      width: 14px;
      height: 14px;
    }
  }
`;

const Footer = () => {
  const [githubInfo, setGitHubInfo] = useState({
    stars: null,
    forks: null,
  });

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      return;
    }
    fetch('https://api.github.com/users/NoorUlBaseer/repos')
      .then(response => response.json())
      .then(json => {
        const { stargazers_count, forks_count } = json;
        setGitHubInfo({
          stars: stargazers_count,
          forks: forks_count,
        });
      })
      .catch(e => console.error(e));
  }, []);

  return (
    <StyledFooter>
      <div className="social-links">
        <ul>
          {socialMedia &&
            socialMedia.map(({ name, url }, i) => (
              <li key={i}>
                <a href={url} aria-label={name} target="_blank" rel="noreferrer">
                  <Icon name={name} />
                </a>
              </li>
            ))}
        </ul>
      </div>

      <div className="email-link-footer">
        <a href={`mailto:${email}`}>{email}</a>
      </div>

      <StyledCredit tabindex="-1">
        <div>
          <a href="https://github.com/NoorUlBaseer">
            <div>Designed & Built by Noor Ul Baseer 🧑‍💻</div>
            {githubInfo.stars && githubInfo.forks && (
              <div className="github-stats">
                <span>
                  <Icon name="Star" />
                  <span>{githubInfo.stars.toLocaleString()}</span>
                </span>
                <span>
                  <Icon name="Fork" />
                  <span>{githubInfo.forks.toLocaleString()}</span>
                </span>
              </div>
            )}
          </a>
        </div>
        <br></br>
      </StyledCredit>
    </StyledFooter>
  );
};

Footer.propTypes = {
  githubInfo: PropTypes.object,
};

export default Footer;
